import NotificationBase, {
  NotificationMessage,
  onMessageHandler,
  onClickNotificationHandler,
  TokenType,
} from './NotificationBase';

// ios
const PUSH_PERMISSION_REQUEST = 'push-permission-request';
const PUSH_TOKEN = 'push-token';
const PUSH_NOTIFICATION = 'push-notification';
const PUSH_NOTIFICATION_CLICK = 'push-notification-click';
const PUSH_SET_BADGE_COUNT = 'push-set-badge-count';
const PUSH_PERMISSION_STATE = 'push-permission-state';
const PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL = 'push-clear-notifications-by-channel';
const PUSH_CLEAR_ALL_NOTIFICATIONS = 'push-clear-all-notifications';

export const isIOSEnv = () => {
  return (
    window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[PUSH_PERMISSION_REQUEST]
  );
};

export class IOSNotification extends NotificationBase {
  private messageHandlers: Set<onMessageHandler> = new Set();
  private isMessageSubscribed = false;

  async requestPermission() {
    try {
      const permission = new Promise<NotificationPermission>((resolve) => {
        window.webkit.messageHandlers[PUSH_PERMISSION_REQUEST].postMessage(PUSH_PERMISSION_REQUEST);
        window.addEventListener(
          PUSH_PERMISSION_REQUEST,
          (message: any) => {
            if (!message?.detail) {
              resolve('default');
            }
            switch (message.detail) {
              case 'granted':
                resolve('granted');
                break;
              default:
                resolve('denied');
                break;
            }
          },
          { once: true },
        );
      });
      return permission;
    } catch (error) {
      return 'denied';
    }
  }

  // https://github.com/khmyznikov/ios-pwa-wrap#push-permission-state
  async getPermissionState() {
    try {
      const permission = await new Promise<NotificationPermission>((resolve) => {
        window.webkit.messageHandlers[PUSH_PERMISSION_STATE].postMessage(PUSH_PERMISSION_STATE);
        window.addEventListener(
          PUSH_PERMISSION_STATE,
          (message: any) => {
            if (!message?.detail) {
              resolve('default');
            }
            switch (message.detail) {
              case 'notDetermined':
                resolve('default');
                break;
              case 'denied':
                resolve('denied');
                break;
              case 'authorized':
              case 'ephemeral':
              case 'provisional':
                resolve('granted');
                break;
              case 'unknown':
              default:
                resolve('denied');
                break;
            }
          },
          { once: true },
        );
      });
      return permission;
    } catch (error) {
      return 'denied';
    }
  }

  async requestToken(): Promise<TokenType | null> {
    try {
      const token = await new Promise<string>((resolve) => {
        window.webkit.messageHandlers[PUSH_TOKEN].postMessage(PUSH_TOKEN);
        window.addEventListener(
          PUSH_TOKEN,
          (message: any) => {
            resolve(message?.detail);
          },
          { once: true },
        );
      });
      return { platform: 'ios', token };
    } catch (error) {
      return null;
    }
  }

  async onMessage(handler: onMessageHandler) {
    this.messageHandlers.add(handler);

    // 이벤트 리스너는 한 번만 등록하고, 모든 핸들러에 메시지 분배
    if (!this.isMessageSubscribed) {
      this.isMessageSubscribed = true;
      window.addEventListener(PUSH_NOTIFICATION, (message: any) => {
        const notificationMessage = message.detail as NotificationMessage;
        this.messageHandlers.forEach((h) => {
          try {
            h(notificationMessage);
          } catch (e) {
            console.error('Error in notification message handler:', e);
          }
        });
      });
    }

    // 개별 핸들러 해제 함수 반환
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  setBadgeCount(count: number) {
    window.webkit.messageHandlers[PUSH_SET_BADGE_COUNT].postMessage({
      count,
    });
  }

  /**
   * iOS 네이티브 앱에 특정 channelId의 푸시 알림들을 정리하도록 요청합니다.
   * 네이티브 측에서 동일 이벤트명(PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL)으로 응답 이벤트를 보내주는 경우
   * 1회성으로 대기한 뒤 resolve 합니다. 응답이 없다면 fire-and-forget으로 동작합니다.
   */
  async clearNotificationsByChannel(channelId: string): Promise<void> {
    try {
      // fire the request to native
      window.webkit.messageHandlers[PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL].postMessage({ channelId });

      // optional: wait for a single acknowledge event if native posts back
      await new Promise<void>((resolve) => {
        let timeoutId: number | undefined;
        const handler = () => {
          if (timeoutId) window.clearTimeout(timeoutId);
          window.removeEventListener(PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL, handler as any);
          resolve();
        };
        // best-effort: auto-resolve after a short timeout if no ack
        timeoutId = window.setTimeout(() => {
          window.removeEventListener(PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL, handler as any);
          resolve();
        }, 1500);
        window.addEventListener(PUSH_CLEAR_NOTIFICATIONS_BY_CHANNEL, handler as any, { once: true });
      });
    } catch (e) {
      // noop: iOS 환경이 아니거나 messageHandler 미구현 시에도 앱이 깨지지 않도록
    }
  }

  /**
   * iOS 네이티브 앱에 모든 푸시 알림들을 정리하도록 요청합니다.
   */
  async clearAllNotifications(): Promise<void> {
    try {
      window.webkit.messageHandlers[PUSH_CLEAR_ALL_NOTIFICATIONS].postMessage(PUSH_CLEAR_ALL_NOTIFICATIONS);
      await new Promise<void>((resolve) => {
        let timeoutId: number | undefined;
        const handler = () => {
          if (timeoutId) window.clearTimeout(timeoutId);
          window.removeEventListener(PUSH_CLEAR_ALL_NOTIFICATIONS, handler as any);
          resolve();
        };
        timeoutId = window.setTimeout(() => {
          window.removeEventListener(PUSH_CLEAR_ALL_NOTIFICATIONS, handler as any);
          resolve();
        }, 1500);
        window.addEventListener(PUSH_CLEAR_ALL_NOTIFICATIONS, handler as any, { once: true });
      });
    } catch (e) {
      // noop
    }
  }

  onNotificationClick(handler: onClickNotificationHandler) {
    function onNotification(message: any) {
      const data = message?.detail as NotificationMessage | undefined;
      if (!data) return;
      if (data.channelId) {
        handler({ ...data, channelId: data.channelId });
      }
    }

    window.addEventListener(PUSH_NOTIFICATION_CLICK, onNotification);
    return () => {
      window.removeEventListener(PUSH_NOTIFICATION_CLICK, onNotification);
    };
  }

  supportsNotifications(): boolean {
    return true;
  }

  setActiveChannel(channelId: string | null): void {
    // iOS 네이티브 앱이 아직 이 이벤트를 핸들하지 않음 → no-op.
    // 향후 iOS 네이티브에서 동일 로직 구현하려면 여기에 webkit.messageHandlers
    // 경유로 보내면 됨.
    void channelId;
  }
}

export default IOSNotification;
